import { isEqual } from 'lodash';
import { FC, useEffect, useRef, useState } from 'react';
import { useCommunications } from '../../../../../../hooks/use-communications';
import { usePatientProfile } from '../../../../../../hooks/use-patient-profile';
import { EventInboxCommunicationsWidget } from '../../../../../../types/event-bus';
import { CommunicationsSlideout } from '../../../communications-widget';
import { CommunicationsFilters } from '../../../communications-widget/communications-filters-component';
import { CommunicationStatus } from '../../../communications-widget/communications-status-component';
import { useCommunicationsStatus } from '../../../communications-widget/use-communications-status';
import { PatientProfileHeader } from '../../header/header';
import { PhoneOnlyPatientProfileHeader } from '../../header/phone-only-header';
import { QuickNavMenu } from '../../header/quick-nav-menu/quick-nav-menu';
import { usePatientProfileWidget } from '../../hooks/use-patient-profile-widget';
import { WidgetRoutePath } from '../../navigation/route-config';
import { ViewContent } from '../shared/view-content/view-content';

export const InboxView: FC = () => {
  const { patientInfo, locationId } = usePatientProfile();
  const { patientInboxEvent, patientIdEvent, patientPhoneEvent, setBreadcrumbExtensions, goTo } =
    usePatientProfileWidget();
  const { patientPhoneNumbers, setPatientId } = useCommunications();
  const communicationStatus = useCommunicationsStatus(patientPhoneNumbers);
  const [inboxEvent, setInboxEvent] = useState<EventInboxCommunicationsWidget | undefined>();
  const lastEvent = useRef<EventInboxCommunicationsWidget | null>(null);

  useEffect(() => {
    return () => {
      setInboxEvent(undefined);
      setBreadcrumbExtensions(undefined);
    };
  }, []);

  useEffect(() => {
    if (!patientInboxEvent && !patientIdEvent && !patientPhoneEvent) {
      setInboxEvent(undefined);
      lastEvent.current = null;
      return;
    }

    const currentEvent = patientInboxEvent;

    if (currentEvent && !isEqual(currentEvent, lastEvent.current)) {
      setInboxEvent(currentEvent);
      lastEvent.current = currentEvent;
    } else if (!currentEvent && lastEvent.current === null) {
      createInboxEvent();
    }
  }, [patientInboxEvent, patientIdEvent, patientPhoneEvent, patientInfo]);

  const createInboxEvent = () => {
    const phoneNums = patientInfo?.phoneNumbers;
    const phoneNumber = phoneNums.cellPhone || phoneNums.homePhone || phoneNums.workPhone;

    if (!phoneNumber) {
      setPatientId(patientInfo?.id);
    } else {
      setPatientId(null);
    }

    const newInboxEvent = {
      messageType: 'inboxCommunications',
      data: {
        patientId: patientInfo.id,
        phoneNumber,
        locationId
      }
    } as EventInboxCommunicationsWidget;

    if (!isEqual(newInboxEvent, lastEvent.current)) {
      lastEvent.current = newInboxEvent;
      setInboxEvent(newInboxEvent);
    }
  };

  useEffect(() => {
    setBreadcrumbExtensions({
      placeholers: {
        left: { component: <CommunicationStatus finalStatus={communicationStatus.finalStatus} /> },
        right: {
          component: (
            <CommunicationsFilters
              {...communicationStatus}
              patientPhoneNumbers={patientPhoneNumbers}
              onClickFilter={() => {
                goTo(WidgetRoutePath.INBOX_FILTERS);
              }}
            />
          )
        }
      }
    });
  }, [
    patientPhoneNumbers,
    communicationStatus.finalStatus,
    communicationStatus.patientMailboxIds,
    communicationStatus.channelsStatuses
  ]);

  return (
    <>
      {patientInfo ? (
        <PatientProfileHeader />
      ) : (
        <PhoneOnlyPatientProfileHeader phoneNumber={inboxEvent?.data.phoneNumber || ''} />
      )}
      <QuickNavMenu />
      <ViewContent>
        {inboxEvent && (
          <CommunicationsSlideout
            allowReadOnly
            phoneNumber={inboxEvent.data.phoneNumber}
            patientId={inboxEvent.data.patientId}
            locationId={inboxEvent.data.locationId!}
            timestamp={inboxEvent.data.timestamp}
            initialMessage={inboxEvent.data.message}
          />
        )}
      </ViewContent>
    </>
  );
};
